/**
 * vue-hotkey.js
 *      Allows for easier management of hotkeys (aka keyboard shortcuts).
 *
 *      Each event listener is added to the window object, but a reference to it is stored inside the component that calls this plugin.
 *      Whenever a component is destroyed, all stored event listeners are automatically removed.
 *
 * Instance Methods:
 *      $keydown: Adds an event listener for the keydown event. If no arguments are passed, then all listeners are removed.
 *          Args:
 *              keys { String | Array }: The key(s) to listen for.
 *              callback { Function | Array }: The callback(s) to fire when the keys are pressed.
 *
 *      $keyup: Adds an event listener for the keyup event. If no arguments are passed, then all listeners are removed.
 *          Args:
 *              keys { String | Array }: The key(s) to listen for.
 *              callback { Function | Array }: The callback(s) to fire when the keys are released.
 *
 * Examples:
 *      // calls someFunc when the enter key is pressed
 *      this.$keydown('enter', someFunc);
 *
 *      // calls someFunc when the enter key is released
 *      this.$keyup('enter', someFunc);
 *
 *      // calls someFunc when the "ctrl" and "a" keys are pressed simultaneously
 *      this.$keydown(['ctrl', 'a'], someFunc);
 *
 *      // removes all keydown listeners
 *      this.$keydown();
 *
 *      // removes all keyup listeners
 *      this.$keyup();
 */

function getIdentifier(keys) {
    return [].concat(keys).toString().toLowerCase();
}

function isKeyMatch(event, keys) {
    const modKeyMap = {
        alt: 'altKey',
        ctrl: 'ctrlKey',
        meta: 'metaKey',
        shift: 'shiftKey',
        tab: 'tabKey',
    };

    const keysArray = [].concat(keys).map(k => k.toLowerCase());

    const modKeys = keysArray.filter(k => Object.keys(modKeyMap).includes(k)).map(k => modKeyMap[k]);
    const regKeys = keysArray.filter(k => !Object.keys(modKeyMap).includes(k));

    const modKeysPressed = modKeys.length ? modKeys.every(modKey => event[modKey]) : true;
    const regKeysPressed = regKeys.length ? regKeys.every(regKey => event.key.toLowerCase() === regKey) : true;

    return modKeysPressed && regKeysPressed;
}

// adds a new keyboard shortcut
function add(eventName, keys, callbacks) {
    const identifier = getIdentifier(keys);

    // always force an array, in case one wasn't passed in
    [].concat(callbacks).forEach(function (callback) {
        const wrappedCallback = function (event) {
            if (isKeyMatch(event, keys)) {
                event.preventDefault();
                callback();
            }
        };

        // set the event listener
        window.addEventListener(eventName, wrappedCallback, true);

        // store a local reference (so we can remove it later)
        this.$hotkeys[eventName][identifier] = wrappedCallback;
    }.bind(this));
}

function remove(eventName) {
    Object.keys(this.$hotkeys[eventName]).forEach(function (identifier) {
        window.removeEventListener(eventName, this.$hotkeys[eventName][identifier], true);

        delete this.$hotkeys[eventName][identifier];
    }.bind(this));
}

export default {
    install(Vue) {
        // lifecycle hooks
        Vue.mixin({
            beforeCreate() {
                this.$hotkeys = {
                    keydown: {},
                    keyup: {},
                };
            },

            destroyed() {
                if (Array.isArray(this.$hotkeys)) {
                    remove.call(this, 'keyup');
                    remove.call(this, 'keydown');
                }
            },
        });

        // instance methods
        Vue.prototype.$keydown = function (keys, callbacks) {
            if (!keys) {
                remove.call(this, 'keydown');
                return;
            }

            add.call(this, 'keydown', keys, callbacks);
        };

        Vue.prototype.$keyup = function (keys, callbacks) {
            if (!keys) {
                remove.call(this, 'keyup');
                return;
            }

            add.call(this, 'keyup', keys, callbacks);
        };
    },
};
